import React, { useEffect, useState, useRef } from "react";
import { CheckCircle2, XCircle, Loader2, ArrowRight, RefreshCw, AlertCircle, ShieldCheck, Mail, ArrowLeft } from "lucide-react";
import BizNearYouLogo from "./BizNearYouLogo";

import { logger } from "../utils/debugLogger";

interface BillingSuccessPageProps {
  currentUser: any;
  apiFetch: (url: string, options?: RequestInit) => Promise<Response>;
  loadBillingStatus: () => Promise<void>;
  onBackToApp: () => void;
}

export function BillingSuccessPage({
  currentUser,
  apiFetch,
  loadBillingStatus,
  onBackToApp,
}: BillingSuccessPageProps) {
  const [activationState, setActivationState] = useState<"checking" | "syncing" | "active" | "delayed">("checking");
  const [billingInfo, setBillingInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const conversionTrackedRef = useRef(false);

  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  // Function to trigger Google Ads purchase conversion
  const triggerGoogleAdsConversion = (sessId: string | null) => {
    if (conversionTrackedRef.current) return;
    conversionTrackedRef.current = true;

    logger.debug("[Google Ads Tracking] Pro status confirmed. Triggering conversion event...", {
      transaction_id: sessId || "unknown_session",
      value: 29.0,
      currency: "USD",
    });

    // Fire standard gtag purchase/conversion if present
    if (typeof (window as any).gtag === "function") {
      try {
        (window as any).gtag("event", "conversion", {
          send_to: "AW-CONVERSION_ID/CONVERSION_LABEL", // Standard configured tracking placeholder
          value: 29.0,
          currency: "USD",
          transaction_id: sessId || "",
        });
      } catch (e) {
        console.warn("[Google Ads Tracking] Failed to invoke gtag function safely:", e);
      }
    }

    // Also push to dataLayer as standard practice
    if ((window as any).dataLayer && Array.isArray((window as any).dataLayer)) {
      try {
        (window as any).dataLayer.push({
          event: "purchase",
          ecommerce: {
            transaction_id: sessId || "stripe_" + Math.random().toString(36).substr(2, 9),
            value: 29.0,
            currency: "USD",
            items: [
              {
                item_name: "BizNearYou Pro Subscription",
                price: 29.0,
                quantity: 1,
              }
            ]
          }
        });
      } catch (e) {
        console.warn("[Google Ads Tracking] Failed to push to dataLayer:", e);
      }
    }
  };

  const checkAndActivate = async (isManualRetry = false) => {
    if (isManualRetry) {
      setIsRetrying(true);
      setError(null);
    }

    try {
      // 1. Check current status
      const statusRes = await apiFetch("/api/billing/status");
      if (!statusRes.ok) {
        throw new Error("Could not retrieve current billing configuration status.");
      }
      const statusData = await statusRes.json();

      if (statusData?.isPro) {
        setBillingInfo(statusData);
        setActivationState("active");
        triggerGoogleAdsConversion(sessionId);
        await loadBillingStatus(); // Update core App billing context
        if (isManualRetry) setIsRetrying(false);
        return true;
      }

      // 2. If not Pro, trigger stripe sync
      setActivationState("syncing");
      const syncRes = await apiFetch("/api/billing/sync-stripe", { method: "POST" });
      
      // 3. Immediately re-check status after sync
      const statusRes2 = await apiFetch("/api/billing/status");
      const statusData2 = await statusRes2.json();

      if (statusData2?.isPro) {
        setBillingInfo(statusData2);
        setActivationState("active");
        triggerGoogleAdsConversion(sessionId);
        await loadBillingStatus(); // Update core App billing context
        if (isManualRetry) setIsRetrying(false);
        return true;
      }

      if (isManualRetry) {
        setIsRetrying(false);
        // If manual retry still didn't make user Pro, let them know activation is delayed
        setActivationState("delayed");
      }
      return false;
    } catch (err: any) {
      console.error("[BillingSuccess] Error checking Pro status:", err);
      setError(err.message || "An unexpected error occurred during Pro activation.");
      if (isManualRetry) setIsRetrying(false);
      return false;
    }
  };

  // On initial mount, start the verification and polling loop
  useEffect(() => {
    let active = true;
    let pollInterval: NodeJS.Timeout | null = null;
    let currentAttempts = 0;

    const runInit = async () => {
      const isConfirmedPro = await checkAndActivate();
      if (isConfirmedPro || !active) return;

      // Start automatic polling loop if not confirmed Pro yet
      pollInterval = setInterval(async () => {
        currentAttempts++;
        setAttempts(currentAttempts);

        if (currentAttempts >= 8) {
          // If exceeded 8 attempts (approx 16-20 seconds), mark as delayed
          if (active) {
            setActivationState("delayed");
          }
          if (pollInterval) clearInterval(pollInterval);
          return;
        }

        try {
          const statusRes = await apiFetch("/api/billing/status");
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData?.isPro) {
              setBillingInfo(statusData);
              if (active) {
                setActivationState("active");
                triggerGoogleAdsConversion(sessionId);
                await loadBillingStatus();
              }
              if (pollInterval) clearInterval(pollInterval);
            }
          }
        } catch (e) {
          console.warn("[BillingSuccess] Polling check failed:", e);
        }
      }, 2500);
    };

    runInit();

    return () => {
      active = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4 md:p-6 font-sans relative overflow-hidden">
      {/* Abstract background glow design to maintain highly polished look */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-md w-full bg-[#1E293B] border border-slate-700/60 rounded-2xl p-8 shadow-2xl relative z-10 text-center space-y-6">
        <div className="flex flex-col items-center">
          <BizNearYouLogo textColor="text-white" className="scale-110 mb-4" />
          <h2 className="text-xl font-bold text-white tracking-tight">
            {activationState === "active" ? "Subscription Activated!" : "Confirming Subscription"}
          </h2>
        </div>

        {/* Dynamic State UI */}
        {activationState === "active" ? (
          <div className="space-y-5">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-2 bg-emerald-500/20 rounded-full blur-sm animate-pulse"></div>
                <CheckCircle2 className="w-16 h-16 text-emerald-400 relative z-10" />
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-slate-200 text-sm font-medium">
                {billingInfo?.subscriptionStatus === "trialing" 
                  ? "Your 7-day free trial has been initiated. $0 charged today!" 
                  : "Payment received — activating your BizNearYou Pro subscription."}
              </p>
              <p className="text-emerald-400 text-base font-bold">
                {billingInfo?.subscriptionStatus === "trialing" 
                  ? "Your BizNearYou Pro 7-Day Free Trial is active!" 
                  : "Your BizNearYou Pro subscription is active!"}
              </p>
            </div>

            <p className="text-slate-400 text-xs">
              {billingInfo?.subscriptionStatus === "trialing" 
                ? "Enjoy unrestricted access to all Pro features absolutely free. Cancel anytime before day 7 to avoid being charged." 
                : "Thank you for choosing BizNearYou Pro! Your limits have been upgraded and full features unlocked."}
            </p>

            <button
              onClick={onBackToApp}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-extrabold py-3 px-6 rounded-xl text-xs transition duration-200 tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg hover:shadow-teal-500/20 mt-4 uppercase"
            >
              Enter Pro Workspace <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : activationState === "delayed" ? (
          <div className="space-y-5">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-2 bg-amber-500/20 rounded-full blur-sm"></div>
                <AlertCircle className="w-16 h-16 text-amber-400 relative z-10" />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-slate-200 text-sm font-medium leading-relaxed">
                Stripe payment has completed, but your local account subscription activation is taking slightly longer than expected.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs rounded-lg text-left font-medium">
                {error}
              </div>
            )}

            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 text-slate-300 text-xs leading-relaxed space-y-2 text-left">
              <p className="font-semibold text-slate-200">Safe Fallback Options:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Click the <strong className="text-white">Retry Activation</strong> button below.</li>
                <li>Refresh the webpage to re-verify status.</li>
                <li>Contact support at <a href="mailto:support@biznearyou.com" className="text-teal-400 hover:underline">support@biznearyou.com</a> with your email if delay persists.</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => checkAndActivate(true)}
                disabled={isRetrying}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 font-bold py-2.5 px-4 rounded-xl text-xs transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isRetrying ? "animate-spin" : ""}`} />
                {isRetrying ? "Verifying..." : "Retry Activation"}
              </button>
              
              <button
                onClick={onBackToApp}
                className="flex-1 bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white font-bold py-2.5 px-4 rounded-xl text-xs transition flex items-center justify-center gap-1 cursor-pointer"
              >
                Go to Workspace <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <div className="relative">
                <Loader2 className="w-16 h-16 text-teal-400 animate-spin" />
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-slate-200 text-sm font-semibold animate-pulse">
                Payment received — activating your BizNearYou Pro subscription...
              </p>
              <p className="text-slate-400 text-xs">
                We are synchronizing secure billing credentials from Stripe Gateway. This should take just a brief moment.
              </p>
            </div>

            <div className="text-[11px] text-slate-500 font-mono">
              Status verification attempt #{attempts + 1}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface BillingCancelPageProps {
  onReturnToPricing: () => void;
}

export function BillingCancelPage({ onReturnToPricing }: BillingCancelPageProps) {
  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4 md:p-6 font-sans relative overflow-hidden">
      {/* Abstract background glow design */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-md w-full bg-[#1E293B] border border-slate-700/60 rounded-2xl p-8 shadow-2xl relative z-10 text-center space-y-6 animate-scale-up">
        <div className="flex flex-col items-center">
          <BizNearYouLogo textColor="text-white" className="scale-110 mb-4" />
          <h2 className="text-xl font-bold text-white tracking-tight">Checkout Canceled</h2>
        </div>

        <div className="space-y-5">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute -inset-2 bg-rose-500/10 rounded-full blur-sm"></div>
              <XCircle className="w-16 h-16 text-rose-400 relative z-10" />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-slate-200 text-sm font-semibold">
              Checkout process was canceled or closed.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Your Stripe Checkout session was closed and no payment transactions were authorized. Your account status remains under the Free tier.
            </p>
          </div>

          <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/60 text-slate-300 text-xs leading-relaxed text-left">
            <span className="font-semibold text-slate-200 block mb-1">Need help?</span>
            If you experienced an error or have questions about our plans, please try subscribing again or feel free to contact us at support.
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={onReturnToPricing}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-extrabold py-3 px-6 rounded-xl text-xs transition duration-200 tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg uppercase"
            >
              <ArrowLeft className="w-4 h-4" /> View Premium Plans
            </button>
            <button
              onClick={onReturnToPricing} // Can just trigger app return
              className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-bold py-2.5 px-4 rounded-xl text-xs transition flex items-center justify-center gap-2 border border-slate-700 cursor-pointer"
            >
              Return to App Workspace
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
